/**
 * Transaction History Module
 *
 * Stores and retrieves transaction history from localStorage.
 * Tracks pending, confirmed, and failed transactions.
 */

import { type Platform } from './trading';

// ============================================================================
// Types
// ============================================================================

export type TransactionStatus = 'pending' | 'confirmed' | 'failed';

export interface TransactionRecord {
  id: string;
  signature: string;
  tokenAddress: string;
  tokenSymbol: string;
  tokenName: string;
  action: 'buy' | 'sell';
  inputAmount: number; // SOL for buy, tokens for sell
  outputAmount: string; // Expected output
  platform: Platform;
  status: TransactionStatus;
  timestamp: number;
  explorerUrl: string;
  errorMessage?: string;
  confirmedAt?: number;
}

// ============================================================================
// Storage
// ============================================================================

const STORAGE_KEY = 'skop_tx_history';
const MAX_HISTORY_SIZE = 100;

/**
 * Get all transaction history
 */
export function getHistory(): TransactionRecord[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Save transaction history
 */
function saveHistory(history: TransactionRecord[]): void {
  try {
    // Keep only the most recent MAX_HISTORY_SIZE transactions
    const trimmed = history.slice(0, MAX_HISTORY_SIZE);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch (e) {
    console.error('Failed to save transaction history:', e);
  }
}

/**
 * Add a new transaction to history
 */
export function addTransaction(tx: Omit<TransactionRecord, 'id' | 'timestamp'>): TransactionRecord {
  const history = getHistory();

  const record: TransactionRecord = {
    ...tx,
    id: generateId(),
    timestamp: Date.now(),
  };

  history.unshift(record);
  saveHistory(history);

  return record;
}

/**
 * Update a transaction's status
 */
export function updateTransaction(
  id: string,
  updates: Partial<Pick<TransactionRecord, 'status' | 'errorMessage' | 'confirmedAt'>>
): TransactionRecord | null {
  const history = getHistory();
  const index = history.findIndex(tx => tx.id === id);

  if (index === -1) return null;

  history[index] = { ...history[index], ...updates };
  saveHistory(history);

  return history[index];
}

/**
 * Update transaction by signature
 */
export function updateTransactionBySignature(
  signature: string,
  updates: Partial<Pick<TransactionRecord, 'status' | 'errorMessage' | 'confirmedAt'>>
): TransactionRecord | null {
  const history = getHistory();
  const index = history.findIndex(tx => tx.signature === signature);

  if (index === -1) return null;

  history[index] = { ...history[index], ...updates };
  saveHistory(history);

  return history[index];
}

/**
 * Get pending transactions
 */
export function getPendingTransactions(): TransactionRecord[] {
  return getHistory().filter(tx => tx.status === 'pending');
}

/**
 * Get transactions for a specific token
 */
export function getTokenTransactions(tokenAddress: string): TransactionRecord[] {
  return getHistory().filter(tx => tx.tokenAddress === tokenAddress);
}

/**
 * Get recent transactions (last N)
 */
export function getRecentTransactions(limit: number = 10): TransactionRecord[] {
  return getHistory().slice(0, limit);
}

/**
 * Clear all history
 */
export function clearHistory(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error('Failed to clear history:', e);
  }
}

/**
 * Delete a specific transaction
 */
export function deleteTransaction(id: string): boolean {
  const history = getHistory();
  const filtered = history.filter(tx => tx.id !== id);

  if (filtered.length === history.length) {
    return false;
  }

  saveHistory(filtered);
  return true;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generate a unique ID
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Format timestamp for display
 */
export function formatTimestamp(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;

  // Less than a minute
  if (diff < 60000) {
    return 'Just now';
  }

  // Less than an hour
  if (diff < 3600000) {
    const mins = Math.floor(diff / 60000);
    return `${mins}m ago`;
  }

  // Less than a day
  if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000);
    return `${hours}h ago`;
  }

  // Less than a week
  if (diff < 604800000) {
    const days = Math.floor(diff / 86400000);
    return `${days}d ago`;
  }

  // Default to date
  return new Date(timestamp).toLocaleDateString();
}

/**
 * Calculate total volume for a token
 */
export function getTokenVolume(tokenAddress: string): {
  totalBuySol: number;
  totalSellSol: number;
  tradeCount: number;
} {
  const txs = getTokenTransactions(tokenAddress);

  let totalBuySol = 0;
  let totalSellSol = 0;

  for (const tx of txs) {
    if (tx.status !== 'confirmed') continue;

    if (tx.action === 'buy') {
      totalBuySol += tx.inputAmount;
    } else {
      // For sells, outputAmount is SOL
      totalSellSol += parseFloat(tx.outputAmount) / 1e9;
    }
  }

  return {
    totalBuySol,
    totalSellSol,
    tradeCount: txs.filter(tx => tx.status === 'confirmed').length,
  };
}
